import { Stack, Text, Box } from '@ltvco/refresh-lib/theme';

interface UsernameReportRendererProps {
  data: {
    username_presence?: { [key: string]: boolean | string };
    potential_owner?: Array<{
      name?: string;
      age?: string;
      address?: string;
      phone?: string;
      email?: string;
      jobs?: string[];
      education?: string[];
      criminal?: string[];
    }>;
  };
}

const platformDisplayNames: Record<string, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  'x/twitter': 'X (Twitter)',
  snapchat: 'Snapchat',
  reddit: 'Reddit',
  linkedin: 'LinkedIn',
  youtube: 'YouTube',
  telegram: 'Telegram',
  tiktok: 'TikTok',
};

export const UsernameReportRenderer = ({
  data,
}: UsernameReportRendererProps) => {
  const usernamePresence = data.username_presence || {};
  const potentialOwners = data.potential_owner || [];

  // Get platforms where username is present
  // Handle both boolean values and string values like "taken"
  // Note: "available" means the username is NOT taken, so it should NOT be included
  const presentPlatforms = Object.entries(usernamePresence)
    .filter(([_, value]) => {
      if (typeof value === 'boolean') {
        return value === true;
      }
      if (typeof value === 'string') {
        // Only consider "taken" as present (exclude "available")
        const lowerValue = value.toLowerCase();
        return lowerValue === 'taken' || lowerValue === 'true';
      }
      // Also handle truthy values
      return Boolean(value);
    })
    .map(([platform]) => platform);

  // Filter owners that have at least one field with data
  const hasOwnerData = (owner: {
    name?: string;
    age?: string;
    address?: string;
    phone?: string;
    email?: string;
    jobs?: string[];
    education?: string[];
    criminal?: string[];
  }): boolean => {
    return !!(
      (owner.name && owner.name.trim()) ||
      (owner.address && owner.address.trim()) ||
      (owner.phone && owner.phone.trim()) ||
      (owner.email && owner.email.trim()) ||
      (owner.jobs && owner.jobs.length > 0) ||
      (owner.education && owner.education.length > 0) ||
      (owner.criminal && owner.criminal.length > 0)
    );
  };

  const ownersWithData = potentialOwners.filter(hasOwnerData);

  return (
    <Stack spacing={2}>
      <Text variant="body1">
        We found information about this username across{' '}
        <span style={{ fontWeight: 700 }}>
          {presentPlatforms.length} platform
          {presentPlatforms.length !== 1 ? 's' : ''}
        </span>
        .
      </Text>

      <Stack spacing={1.5}>
        {/* Only show Username Presence section if there are platforms */}
        {presentPlatforms.length > 0 && (
          <Box>
            <Text variant="body1" sx={{ fontWeight: 700, mb: 1 }}>
              Username Presence:
            </Text>
            <Text variant="body1">
              {presentPlatforms.map((platform, index) => {
                // Format platform name: use display name if available, otherwise capitalize first letter
                const displayName =
                  platformDisplayNames[platform] ||
                  platform.charAt(0).toUpperCase() + platform.slice(1);
                return (
                  <span key={platform}>
                    <span style={{ fontWeight: 700 }}>{displayName}</span>
                    {index < presentPlatforms.length - 1 && ', '}
                  </span>
                );
              })}
            </Text>
          </Box>
        )}

        {/* Only show Potential Owners section if there are owners with data */}
        {ownersWithData.length > 0 && (
          <Box>
            <Text variant="body1" sx={{ fontWeight: 700, mb: 1 }}>
              Potential Owner{ownersWithData.length !== 1 ? 's' : ''}:
            </Text>
            {ownersWithData.slice(0, 2).map((owner, index) => {
              // Check which fields have data
              const hasName = owner.name && owner.name.trim();
              const hasAddress =
                owner.address &&
                owner.address.trim() &&
                !owner.address.match(/^[, ]+$/);
              const hasPhone = owner.phone && owner.phone.trim();
              const hasEmail = owner.email && owner.email.trim();
              const hasJobs = owner.jobs && owner.jobs.length > 0;
              const hasEducation =
                owner.education && owner.education.length > 0;
              const hasCriminal = owner.criminal && owner.criminal.length > 0;

              // Only show owner if they have at least one field with data
              const hasAnyData =
                hasName ||
                hasAddress ||
                hasPhone ||
                hasEmail ||
                hasJobs ||
                hasEducation ||
                hasCriminal;

              if (!hasAnyData) {
                return null;
              }

              return (
                <Box
                  key={index}
                  mb={index < Math.min(ownersWithData.length, 2) - 1 ? 1.5 : 0}
                >
                  {hasName && (
                    <Text variant="body1">
                      <span style={{ fontWeight: 700 }}>Name:</span>{' '}
                      {owner.name}
                      {owner.age && owner.age.trim() && ` (Age: ${owner.age})`}
                    </Text>
                  )}
                  {hasAddress && (
                    <Text variant="body1">
                      <span style={{ fontWeight: 700 }}>Address:</span>{' '}
                      {owner.address}
                    </Text>
                  )}
                  {hasPhone && (
                    <Text variant="body1">
                      <span style={{ fontWeight: 700 }}>Phone:</span>{' '}
                      {owner.phone}
                    </Text>
                  )}
                  {hasEmail && (
                    <Text variant="body1">
                      <span style={{ fontWeight: 700 }}>Email:</span>{' '}
                      {owner.email}
                    </Text>
                  )}
                  {hasJobs &&
                    owner.jobs &&
                    (() => {
                      const jobsArray = owner.jobs;
                      if (!jobsArray) return null;
                      const validJobs = jobsArray.filter(
                        (job: string) => job && job.trim()
                      );
                      if (validJobs.length === 0) return null;
                      const displayedJobs = validJobs.slice(0, 3);
                      const remainingCount = validJobs.length - 3;
                      return (
                        <Text variant="body1">
                          <span style={{ fontWeight: 700 }}>Jobs:</span>{' '}
                          {displayedJobs.join(', ')}
                          {remainingCount > 0 && ` (+${remainingCount} more)`}
                        </Text>
                      );
                    })()}
                  {hasEducation &&
                    owner.education &&
                    (() => {
                      const educationArray = owner.education;
                      if (!educationArray) return null;
                      const validEducation = educationArray.filter(
                        (edu: string) => edu && edu.trim()
                      );
                      if (validEducation.length === 0) return null;
                      const displayedEducation = validEducation.slice(0, 3);
                      const remainingCount = validEducation.length - 3;
                      return (
                        <Text variant="body1">
                          <span style={{ fontWeight: 700 }}>Education:</span>{' '}
                          {displayedEducation.join(', ')}
                          {remainingCount > 0 && ` (+${remainingCount} more)`}
                        </Text>
                      );
                    })()}
                  {hasCriminal &&
                    owner.criminal &&
                    (() => {
                      const criminalArray = owner.criminal;
                      if (!criminalArray) return null;
                      const validCriminal = criminalArray.filter(
                        (crim: string) => crim && crim.trim()
                      );
                      if (validCriminal.length === 0) return null;
                      const displayedCriminal = validCriminal.slice(0, 3);
                      const remainingCount = validCriminal.length - 3;
                      return (
                        <Text variant="body1">
                          <span style={{ fontWeight: 700 }}>
                            Criminal Records:
                          </span>{' '}
                          {displayedCriminal.join(', ')}
                          {remainingCount > 0 && ` (+${remainingCount} more)`}
                        </Text>
                      );
                    })()}
                </Box>
              );
            })}
            {ownersWithData.length > 2 && (
              <Text variant="body2" sx={{ fontStyle: 'italic', mt: 1 }}>
                And {ownersWithData.length - 2} more potential owner
                {ownersWithData.length - 2 !== 1 ? 's' : ''}...
              </Text>
            )}
          </Box>
        )}
      </Stack>
    </Stack>
  );
};
